import { OrderQuoteVetterJobStatus, useGetOrderQuoteVetterJobsQuery } from '@codezo/graphql';
import { ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';
import { useCurrencyStore } from '../../../stores/currency';
import { Button } from '../../ui/button';
import { ApplyVetterPricesModal } from './ApplyVetterPricesModal';
import type { QuoteLine } from './QuoteLinePicker';
import { SendPriceRequestModal } from './SendPriceRequestModal';
import { VetterJobRow } from './VetterJobRow';

/**
 * Price requests on this quote — one row per supplier (a "job"): which
 * products, where it stands, what they sent back, and the people reached by
 * link. Withdrawn requests stay, since their prices still count, but folded
 * away under a toggle so the live ones lead.
 */
export function QuoteSuppliers({
	quoteId,
	lines,
	editable,
	onPricesApplied,
}: {
	quoteId: string;
	lines: QuoteLine[];
	editable: boolean;
	/** After supplier prices were applied to an offer — the page refreshes what it shows. */
	onPricesApplied: () => void;
}) {
	const [sendOpen, setSendOpen] = useState(false);
	const [showWithdrawn, setShowWithdrawn] = useState(false);
	/* "Use these prices": one supplier's prices for one product. */
	const [applyOpen, setApplyOpen] = useState(false);
	const [applyFor, setApplyFor] = useState<{
		jobId: string;
		itemId: string;
		productName: string;
	} | null>(null);

	// Supplier prices come back converted into this too; theirs stays secondary.
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, refetch } = useGetOrderQuoteVetterJobsQuery({
		variables: { orderQuoteId: quoteId, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});
	const jobs = data?.getOrderQuoteVetterJobs ?? [];
	const active = jobs.filter((j) => j.status === OrderQuoteVetterJobStatus.Active);
	const withdrawn = jobs.filter((j) => j.status !== OrderQuoteVetterJobStatus.Active);

	return (
		<>
			<div className="rounded-[var(--r-3)] border border-line-1 bg-surface">
				{loading && jobs.length === 0 ? (
					<div className="px-4 py-6 text-sm text-text-4">Loading…</div>
				) : jobs.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-text-4">
						No price requests sent. Ask a supplier to price products, then use their
						cost, production time and shipping in an offer.
					</div>
				) : (
					<div className="flex flex-col divide-y divide-line-1">
						{[...active, ...(showWithdrawn ? withdrawn : [])].map((job) => (
							<VetterJobRow
								key={job.id}
								job={job}
								lines={lines}
								editable={editable}
								onChanged={() => refetch()}
								onUsePrices={(itemId, productName) => {
									setApplyFor({ jobId: job.id, itemId, productName });
									setApplyOpen(true);
								}}
							/>
						))}
						{withdrawn.length > 0 && (
							<button
								type="button"
								onClick={() => setShowWithdrawn((v) => !v)}
								aria-expanded={showWithdrawn}
								className="flex items-center gap-1.5 px-4 py-2 text-left text-xs text-text-4 transition-colors hover:text-text-2"
							>
								<ChevronDown
									size={12}
									className={
										showWithdrawn
											? 'rotate-180 transition-transform'
											: 'transition-transform'
									}
								/>
								{showWithdrawn ? 'Hide' : 'Show'} {withdrawn.length} withdrawn
								request
								{withdrawn.length === 1 ? '' : 's'}
							</button>
						)}
					</div>
				)}
				{editable && (
					<div className="border-t border-line-1 px-4 py-2.5">
						<Button variant="ghost" size="sm" onClick={() => setSendOpen(true)}>
							<Plus size={13} data-icon="inline-start" />
							Request prices
						</Button>
					</div>
				)}
			</div>

			<ApplyVetterPricesModal
				open={applyOpen}
				onOpenChange={setApplyOpen}
				quoteId={quoteId}
				job={jobs.find((j) => j.id === applyFor?.jobId) ?? null}
				orderQuoteItemId={applyFor?.itemId ?? null}
				productName={applyFor?.productName ?? ''}
				onApplied={() => {
					// The panel's "used on the offer" labels move with it.
					refetch();
					onPricesApplied();
				}}
			/>

			<SendPriceRequestModal
				open={sendOpen}
				onOpenChange={setSendOpen}
				orderQuoteId={quoteId}
				lines={lines}
				onSuccess={() => refetch()}
			/>
		</>
	);
}
